import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CoreService } from 'src/app/common/http-services/core.service';
import { environment } from 'src/environments/environment';

export type BillingLifecycleAction = 'cancel' | 'undo' | 'feature' | 'close-soft' | 'close-hard' | 'restore' | 'resubscribe' | 'reconcile';

export interface BillingLifecycleCommand {
    operationId: string;
    action: BillingLifecycleAction;
    reason?: string;
    billingProductId?: number;
    remove?: boolean;
    immediate?: boolean;
    priceIds?: string[];
}

export interface BillingLifecycleResult {
    operationId: string;
    status: 'pending' | 'processing' | 'waiting' | 'completed' | 'failed' | 'withdrawn' | 'action-required';
    subscriptionStatus: string | null;
    cancelAtPeriodEnd: boolean;
    effectiveAtUtc: string | null;
    error: string | null;
    clientSecret?: string | null;
}

export interface OrganizationSubscriptionState {
    subscriptionId: string | null;
    stripeCustomerId?: string | null;
    subscriptionStatus: string | null;
    cancelAtPeriodEnd: boolean;
    effectiveAtUtc: string | null;
    pendingOperation: BillingLifecycleResult | null;
}

@Injectable({ providedIn: 'root' })
export class OrganizationLifecycleService {
    public constructor(private readonly core: CoreService<unknown>) { }

    public async submit(organizationId: number, command: BillingLifecycleCommand): Promise<BillingLifecycleResult> {
        const result = await firstValueFrom(this.core.post(`${environment.billingServiceUrl}/organization/${organizationId}/billing/operations`, command)) as BillingLifecycleResult;
        if (result.status === 'failed' || result.status === 'action-required') {
            throw new Error(result.error ?? 'Billing operation needs attention.');
        }
        return result;
    }

    public async operation(organizationId: number, operationId: string): Promise<BillingLifecycleResult> {
        return await firstValueFrom(this.core.get(`${environment.billingServiceUrl}/organization/${organizationId}/billing/operations/${operationId}`)) as BillingLifecycleResult;
    }

    public async state(organizationId: number): Promise<OrganizationSubscriptionState> {
        return await firstValueFrom(this.core.get(`${environment.billingServiceUrl}/organization/${organizationId}/billing/state`)) as OrganizationSubscriptionState;
    }

    public async personalDeletionStatus(): Promise<{ pending: boolean }> {
        return await firstValueFrom(this.core.get(`${environment.identityUrl}/user/me/deletion`)) as { pending: boolean };
    }

    public async withdrawPersonalDeletion(): Promise<void> {
        await firstValueFrom(this.core.delete(`${environment.identityUrl}/user/me/deletion`));
    }

    public async deletePersonalAccount(): Promise<{ deleted: boolean; pending: boolean }> {
        return await firstValueFrom(this.core.post(`${environment.identityUrl}/user/me/deletion`, { waitForClosure: true })) as { deleted: boolean; pending: boolean };
    }
}
